<template>
    <v-form ref="form">
        <FormOptions
            ref="formOptions"
            v-model="formData.transportOptions"
            :formType="formType"
            :title="$t('formFields.options')"
        />

        <FormInfo
            ref="formInfo"
            v-model="formData.info"
            :formType="formType"
            :title="$t('formFields.information')"
        />
        <FormLocation
            ref="loadingLocation"
            data-cy-prefix="loading-location"
            v-model="formData.loadingLocation"
            :formType="formType"
            :title="$t('formFields.loadingAt')"
        />
        <FormLocation
            ref="unloadingLocation"
            data-cy-prefix="unloading-location"
            v-model="formData.unloadingLocation"
            :formType="formType"
            :title="$t('formFields.unloadingAt')"
        />
        <RouteMap
            data-cy-prefix="publish-form"
            :origin-place-id="formData.loadingLocation?.place_id"
            :destination-place-id="formData.unloadingLocation?.place_id"
        />
        <FormNotes ref="formNotes" v-model="formData.notes" />
    </v-form>

    <LoadingOverlay :model-value="submitting" />

    <v-divider class="pb-4"></v-divider>
    <!--        <v-row>-->
    <!--            <v-col>-->
    <!--                <v-btn @click="fillForm">FILL FORM</v-btn>-->
    <!--            </v-col>-->
    <!--        </v-row>-->
</template>

<script>
import { nextTick } from "vue";
import { v4 as uuidv4 } from "uuid";
import FormInfo from "./FormInfo.vue";
import FormLocation from "./FormLocation.vue";
import FormNotes from "./FormNotes.vue";
import FormOptions from "./FormOptions.vue";
import RouteMap from "./RouteMap.vue";
import { mapMutations, mapState } from "vuex";
import FormActions from "./FormActions.vue";
import LoadingOverlay from "@/components/display/LoadingOverlay.vue";
import axios from "axios"; // Ensure axios is installed via npm
import { mapEnumValue, showAlertModal } from "@/utils";
import {
    PAYMENT_DEADLINE,
    TRUCK_COATING,
    TRUCK_TRANSPORT_TYPE,
    TRUCK_SPECIAL_FEATURE,
    TRUCK_TYPE,
} from "@/enums/truckEnums.js";
import emitter from "@/eventBus";

export default {
    props: {
        formType: String,
        required: true,
        editMode: {
            type: Boolean,
            default: false,
        },
        entriesData: {
            type: Object,
            default: null,
        },
    },
    components: {
        FormInfo,
        FormLocation,
        FormNotes,
        FormOptions,
        FormActions,
        RouteMap,
        LoadingOverlay,
    },
    data() {
        return {
            submitting: false,
            formData:
                this.editMode && this.entriesData
                    ? {
                          ...this.entriesData,
                          transportOptions: {
                              truckTransportType:
                                  this.entriesData.transport_options
                                      ?.truck_transport_type || null,
                              truckSpecialFeatures:
                                  this.entriesData.transport_options
                                      ?.truck_special_features || [],
                              truckCoating:
                                  this.entriesData.transport_options
                                      ?.truck_coating || null,
                              truckType:
                                  this.entriesData.transport_options
                                      ?.truck_type || null,
                              platformType:
                                  this.entriesData.transport_options
                                      ?.platform_type || [],
                              dangerousLoad:
                                  this.entriesData.transport_options
                                      ?.dangerous_load || false,
                              escrowAccount:
                                  this.entriesData.transport_options
                                      ?.escrow_account || false,
                          },
                      } // Use existing data if editing
                    : {
                          uid: uuidv4(),
                          formType: this.formType,
                          // Initialize with empty/default values
                          loadingLocation: JSON.parse(
                              JSON.stringify(this.createEmptyLocation())
                          ),
                          unloadingLocation: JSON.parse(
                              JSON.stringify(this.createEmptyLocation())
                          ),
                          info: {
                              weight: "",
                              volume: "",
                              mdp: "",
                              price: "",
                              paymentTerm: 1,
                          },
                          notes: "",
                          transportOptions: {
                              truckSpecialFeatures: [],
                              truckTransportType: 1,
                              truckCoating: 1,
                              dangerousLoad: false,
                              platformType: [],
                          },
                      },
            truckRequests: [],
            wareRequests: [],
        };
    },
    computed: {
        ...mapState(["isMobile", "formButtons"]),
        submitRequested() {
            return this.$store.state.submitRequested;
        },
    },
    mounted() {
        this.SET_FORM_BUTTONS([
            {
                label: "common.save", // i18n key
                event: "save-new-entry",
                color: "primary",
                dataCy: 'save-new-entry',
            },
        ]);
        emitter.on("save-new-entry", this.validateForm);
    },
    unmounted() {
        emitter.off("save-new-entry", this.validateForm);
        this.RESET_FORM_BUTTONS();
    },
    methods: {
        showAlertModal,
        ...mapMutations(["SET_FORM_BUTTONS", "RESET_FORM_BUTTONS"]),
        fillForm() {
            this.formData = {
                uid: uuidv4(),
                formType: this.formType,
                loadingLocation: {
                    country: 798549, // Must be a number (not "US")
                    city: "", // City initially empty
                    postalCode: "10001",
                    date: ["2025-08-01"],
                    dateStart: "2025-08-01",
                    dateEnd: "2025-08-01",
                    timeStart: "09:00",
                    timeEnd: "17:00",
                },
                unloadingLocation: {
                    country: 2921044,
                    city: "",
                    postalCode: "90001",
                    date: ["2025-08-03"],
                    dateStart: "2025-08-03",
                    dateEnd: "2025-08-03",
                    timeStart: "10:00",
                    timeEnd: "18:00",
                },
                info: {
                    weight: 20,
                    volume: 30,
                    mdp: 40,
                    price: 500,
                    paymentTerm: 2,
                    currency: "EUR",
                    vatType: true,
                },
                notes: "This is a sample note.",
                transportOptions:
                    this.formType === "truck"
                        ? {
                              truckTransportType: 2,
                              truckCoating: 2,
                              truckType: 1,
                              truckSpecialFeatures: [1],
                              dangerousLoad: false,
                          }
                        : {
                              platformType: [2],
                              truckCoating: 2,
                              dangerousLoad: false,
                              truckTransportType: 2,
                          },
            };
        },
        getTruckType(value) {
            return mapEnumValue(TRUCK_TYPE, value);
        },
        getTransportType(value) {
            return mapEnumValue(TRUCK_TRANSPORT_TYPE, value);
        },
        getSpecialFeature(value) {
            return mapEnumValue(TRUCK_SPECIAL_FEATURE, value);
        },
        getCoating(value) {
            return mapEnumValue(TRUCK_COATING, value);
        },
        getPaymentTerm(value) {
            return mapEnumValue(PAYMENT_DEADLINE, value);
        },
        /**
         * Creates a properly structured location object.
         * @returns {Object} - Empty location structure.
         */
        createEmptyLocation() {
            return {
                country_code: "",
                place_id: "",
                place_display_name: "",
                postalCode: "",
                date: [],
                dateStart: null,
                dateEnd: null,
                timeStart: null,
                timeEnd: null,
            };
        },
        // * Handles form submission logic.
        //  */
        async validateForm() {
            await nextTick(); // Ensures all refs are available
            let isValid = true;

            if (this.$refs.form) {
                const formValid = await this.$refs.form.validate();
                if (!formValid) {
                    isValid = false;
                }
            }

            if (this.$refs.formInfo?.validateFields) {
                const infoValid = await this.$refs.formInfo.validateFields();
                if (!infoValid) {
                    isValid = false;
                }
            }

            if (this.$refs.formOptions?.validateFields) {
                const optionsValid =
                    await this.$refs.formOptions.validateFields();
                if (!optionsValid) {
                    isValid = false;
                }
            }

            if (this.$refs.loadingLocation?.validateFields) {
                const loadingValid =
                    await this.$refs.loadingLocation.validateFields();
                if (!loadingValid) {
                    isValid = false;
                }
            }

            if (this.$refs.unloadingLocation?.validateFields) {
                const unloadingValid =
                    await this.$refs.unloadingLocation.validateFields();
                if (!unloadingValid) {
                    isValid = false;
                }
            }

            if (isValid) {
                await this.submitFormTemporar();
            } else {
                this.showAlertModal(
                    this.$store,
                    this.$t("validation.validationFailed"),
                    "warning",
                    5000
                );
            }
        },
        /**
         * Resets the form to its initial state and clears validation errors.
         */
        resetForm() {
            // Reset form data
            this.formData = {
                uid: uuidv4(),
                formType: this.formType,
                loadingLocation: this.createEmptyLocation(),
                unloadingLocation: this.createEmptyLocation(),
                info: {
                    weight: "",
                    volume: "",
                    mdp: "",
                    price: "",
                    currency: "EUR",
                    vatType: false,
                    paymentTerm: 1,
                },
                notes: "",
                transportOptions: {
                    truckCoating: 1,
                    truckTransportType: 1,
                    truckType: "",
                    truckSpecialFeatures: "",
                    dangerousLoad: false,
                    platformType: [],
                },
            };

            // Reset validation
            if (this.$refs.form) this.$refs.form.resetValidation();
            if (this.$refs.formInfo) this.$refs.formInfo.resetValidation();
            if (this.$refs.formOptions)
                this.$refs.formOptions.resetValidation();
            if (this.$refs.loadingLocation)
                this.$refs.loadingLocation.resetValidation();
            if (this.$refs.unloadingLocation)
                this.$refs.unloadingLocation.resetValidation();

            // Reset fields in child components
            if (this.$refs.formInfo) this.$refs.formInfo.resetFields();
            if (this.$refs.formOptions) this.$refs.formOptions.resetFields();
        },
        /**
         * Cleans a location object by removing unnecessary fields.
         * Ensures that only relevant data is sent.
         */
        async submitFormTemporar() {
            this.submitting = true;
            try {
            const allowedFields = [
                "uid",
                "info",
                "transportOptions",
                "loadingLocation",
                "unloadingLocation",
                "notes",
            ];

            let filteredFormData = Object.fromEntries(
                Object.entries(this.formData).filter(([key]) =>
                    allowedFields.includes(key)
                )
            );

            // Ensure the form type is included
            filteredFormData.form_type = this.formType;

            // Ensure all nested objects are properly structured before sending
            filteredFormData.loading_location = this.cleanLocation(
                filteredFormData.loadingLocation
            );
            filteredFormData.unloading_location = this.cleanLocation(
                filteredFormData.unloadingLocation
            );
            filteredFormData.info = this.cleanInfo(filteredFormData.info);
            filteredFormData.transport_options = this.cleanTransportOptions(
                filteredFormData.transportOptions
            );

            // Determine the endpoint based on form type and edit mode
            let endpoint = "";
            if (this.editMode) {
                // Update (edit) mode: use PATCH
                endpoint =
                    this.formType === "truck"
                        ? `${process.env.VUE_APP_API_URL}/truck-requests/${filteredFormData.uid}/`
                        : `${process.env.VUE_APP_API_URL}/ware-requests/${filteredFormData.uid}/`;
            } else {
                // Create mode: use POST
                endpoint =
                    this.formType === "truck"
                        ? `${process.env.VUE_APP_API_URL}/truck-requests/create/`
                        : `${process.env.VUE_APP_API_URL}/ware-requests/create/`;
            }

            try {
                let response;
                if (this.editMode) {
                    response = await axios.patch(endpoint, filteredFormData, {
                        headers: { "Content-Type": "application/json" },
                    });
                } else {
                    response = await axios.post(endpoint, filteredFormData, {
                        headers: { "Content-Type": "application/json" },
                    });
                }

                // Show success alert
                this.showAlertModal(
                    this.$store,
                    this.$t("validation.formSubmitted"),
                    "success",
                    5000
                );

                // Redirect to correct list based on form type
                if (this.formType === "truck") {
                    this.$router.push({ name: "MyTrucks" }); // Redirect to My Trucks
                } else {
                    this.$router.push({ name: "MyWares" }); // Redirect to My Wares
                }
            } catch (error) {
                this.showAlertModal(
                    this.$store,
                    "Submission failed. Please try again.",
                    "danger",
                    12000,
                    error
                );
            }
            } finally {
                this.submitting = false;
            }
        },

        /**
         * Cleans the location object before submitting to the backend.
         */
        cleanLocation(location) {
            if (!location) return {};
            return {
                country_code: location.country_code || "",
                place_id: location.place_id || null,
                place_display_name: location.place_display_name || "",
                postal_code: location.postalCode || "",
                date_start: location.dateStart || null,
                date_end: location.dateEnd || null,
                date: location.date || [],
                time_start: location.timeStart || null,
                time_end: location.timeEnd || null,
            };
        },

        /**
         * Cleans the info object before sending it to the backend.
         */
        cleanInfo(info) {
            return {
                weight: parseFloat(info.weight) || 0,
                volume: info.volume !== "" ? parseFloat(info.volume) : null,
                mdp: parseFloat(info.mdp) || 0,
                price: info.price !== "" ? parseFloat(info.price) : null,
                payment_term: parseInt(info.paymentTerm) || 1, // Ensure this is an integer
                currency: info.currency,
                vat_type: info.vatType === true,
            };
        },

        /**
         * Cleans the transport options based on the form type.
         */
        cleanTransportOptions(transportOptions) {
            if (!transportOptions) return {};

            return this.formType === "truck"
                ? {
                      truck_transport_type:
                          transportOptions.truckTransportType ?? null,
                      truck_special_features:
                          transportOptions.truckSpecialFeatures ?? [],
                      truck_coating: transportOptions.truckCoating ?? null,
                      truck_type: transportOptions.truckType ?? null,
                  }
                : {
                      truck_coating: transportOptions.truckCoating ?? null,
                      platform_type: transportOptions.platformType ?? [],
                      truck_special_features:
                          transportOptions.truckSpecialFeatures ?? [],
                      escrow_account: transportOptions.escrowAccount ?? false,
                      dangerous_load: transportOptions.dangerousLoad ?? false,
                      truck_transport_type:
                          transportOptions.truckTransportType ?? null,
                  };
        },
    },
    watch: {
        entriesData: {
            handler(newVal) {
                if (this.editMode && newVal) {
                    this.formData = { ...newVal };
                }
            },
            immediate: true,
            deep: true,
        },
        /** Deep watch loadingLocation to ensure proper sync */
        "formData.loadingLocation": {
            handler(newVal) {},
            deep: true,
        },
        "formData.unloadingLocation": {
            handler(newVal) {},
            deep: true,
        },
        submitRequested(newVal) {
            if (newVal) {
                this.validateForm();
                // Reset the flag after submission.
                this.$store.commit("RESET_SUBMIT");
            }
        },
    },
};
</script>
